<template>
  <v-container fluid class="neoparent-new-profile d-flex justify-center">
    <div class="wizard-card">
      <div class="wizard-header">
        <div class="wizard-progress">
          <button
            v-for="step in steps"
            :key="step.number"
            type="button"
            class="wizard-progress__step"
            :class="{
              'wizard-progress__step--active': currentStep === step.number,
              'wizard-progress__step--completed': currentStep > step.number
            }"
            :disabled="isBusy || step.number > highestUnlockedStep"
            @click="goToStep(step.number)"
          >
            <span class="wizard-progress__number">{{ step.number }}</span>
            <span class="wizard-progress__label">{{ step.title }}</span>
          </button>
        </div>
        <div class="wizard-header__content">
          <div class="wizard-header__counter">{{ currentStep }} / {{ steps.length }}</div>
          <h2 class="wizard-header__title">{{ currentStepTitle }}</h2>
        </div>
      </div>

      <div class="error mb-4" v-if="error">
        <p>{{ error }}</p>
      </div>

      <div class="status-message mb-4" v-if="success">
        <p>{{ success }}</p>
      </div>

      <section v-if="currentStep === 1" class="wizard-step">
        <div class="language-switcher">
          <div class="language-switcher__label mb-2">
            {{ $t("app.settings.general.display-language") }}
          </div>
          <v-select
            v-model="selectedLocale"
            :items="languageOptions"
            item-text="label"
            item-value="code"
            outlined
            dense
            hide-details
          />
        </div>

        <div class="wizard-actions wizard-actions--end">
          <LoadingButton v-if="savingLanguage" margin="0"></LoadingButton>
          <Button
            v-else
            width="180px"
            :title="$t('button.next')"
            :disabled="!selectedLocale || isBusy"
            @click.native="continueToProfileStep"
          ></Button>
        </div>
      </section>

      <form v-else class="wizard-step" @submit.prevent="submitForm">
        <div class="field">
          <InputField
            v-model="userData.email"
            disabled
            :labelTitle="$t('app.editProfile.email')"
            type="email"
            required
            :placeholder="$t('app.editProfile.email')"
            margin="0 auto 25px auto"
          ></InputField>
          <InputField
            v-model="userData.nickName"
            :labelTitle="$t('app.editProfile.displayName')"
            required
            :disabled="isBusy"
            :placeholder="$t('app.editProfile.displayName')"
            margin="0 auto 25px auto"
          ></InputField>
          <InputField
            v-model="userData.firstName"
            :labelTitle="$t('app.editProfile.firstName')"
            :disabled="isBusy"
            :placeholder="$t('app.editProfile.firstName')"
            margin="0 auto 25px auto"
          ></InputField>
          <InputField
            v-model="userData.lastName"
            :labelTitle="$t('app.editProfile.lastName')"
            :disabled="isBusy"
            :placeholder="$t('app.editProfile.lastName')"
            margin="0 auto 25px auto"
          ></InputField>
          <InputField
            v-model="userData.phone"
            :labelTitle="$t('app.temp-home.phone')"
            type="tel"
            :disabled="isBusy"
            :placeholder="$t('app.temp-home.phone')"
            margin="0 auto 25px auto"
          ></InputField>
          <TextAreaField
            class="input"
            v-model="userData.bio"
            :labelTitle="$t('app.editProfile.bio')"
            :placeholder="$t('app.editProfile.bio')"
            rows="2"
            style="margin-bottom: 16px"
          />
        </div>

        <div class="wizard-actions wizard-actions--between">
          <button
            type="button"
            class="wizard-back"
            :disabled="isBusy"
            @click="goToStep(1)"
          >
            {{ $t("button.back") }}
          </button>

          <LoadingButton v-if="savingProfile" margin="0"></LoadingButton>
          <Button
            v-else
            width="180px"
            :title="$t('app.editProfile.save')"
            :disabled="isBusy"
          ></Button>
        </div>
      </form>
    </div>
  </v-container>
</template>

<script>
import { neoparentUsers } from "@/api";
import { getSupportedLocales, redirect } from "@/helpers";

export default {
  name: "neoparent-new-profile",
  data() {
    return {
      currentStep: 1,
      highestUnlockedStep: 1,
      selectedLocale: null,
      persistedLocale: null,
      savingLanguage: false,
      savingProfile: false,
      success: "",
      error: "",
      userData: {
        email: "",
        firstName: "",
        lastName: "",
        nickName: "",
        bio: "",
        phone: ""
      }
    };
  },
  computed: {
    isBusy() {
      return this.savingLanguage || this.savingProfile;
    },
    steps() {
      return [
        {
          number: 1,
          title: this.$t("app.settings.general.display-language")
        },
        {
          number: 2,
          title: this.$t("app.editProfile.title")
        }
      ];
    },
    currentStepTitle() {
      const currentStep = this.steps.find(step => step.number === this.currentStep);

      return currentStep ? currentStep.title : "";
    },
    languageOptions() {
      const activeLocale =
        this.selectedLocale || this.persistedLocale || this.normalizeLocale(this.$i18n.locale);

      return getSupportedLocales("", activeLocale).map(code => ({
        code,
        label: this.$t(`general.languages.${code}`)
      }));
    }
  },
  watch: {
    selectedLocale(locale) {
      if (locale) {
        this.applyLocale(locale);
      }
    }
  },
  mounted() {
    const user = this.$store.getters.user || {};
    const initialLocale = this.normalizeLocale(
      user.language || this.$i18n.locale || localStorage.getItem("locale") || "en-GB"
    );

    this.persistedLocale = initialLocale;
    this.selectedLocale = initialLocale;
    this.userData = {
      email: user.email || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      nickName: user.nickName || "",
      bio: user.bio || "",
      phone: user.phone || ""
    };
  },
  methods: {
    normalizeLocale(lang) {
      if (!lang) {
        return null;
      }

      return lang === "be" ? "nl-be" : lang;
    },
    applyLocale(lang) {
      const locale = this.normalizeLocale(lang);

      this.$i18n.locale = locale;
      localStorage.setItem("locale", locale);

      if (this.$moment) {
        this.$moment.locale(locale);
      }
    },
    goToStep(stepNumber) {
      if (this.isBusy || stepNumber > this.highestUnlockedStep) {
        return;
      }

      this.error = "";
      this.success = "";
      this.currentStep = stepNumber;
    },
    getProfilePayload() {
      return {
        firstName: (this.userData.firstName || "").trim(),
        lastName: (this.userData.lastName || "").trim(),
        nickName: (this.userData.nickName || "").trim(),
        bio: (this.userData.bio || "").trim(),
        phone: (this.userData.phone || "").trim()
      };
    },
    syncStoreUser(payload) {
      const user = this.$store.getters.user;

      if (!user) {
        return;
      }

      user.firstName = payload.firstName;
      user.lastName = payload.lastName;
      user.nickName = payload.nickName;
      user.bio = payload.bio;
      user.phone = payload.phone;
      user.language = this.persistedLocale;
    },
    async continueToProfileStep() {
      const locale = this.normalizeLocale(this.selectedLocale);

      this.error = "";
      this.success = "";

      if (!locale) {
        this.error = this.$t("error.general.no-language");
        return;
      }

      this.savingLanguage = true;

      try {
        if (locale !== this.persistedLocale) {
          const result = await neoparentUsers.editUserLanguage(locale);

          if (result && result.data && result.data.parent) {
            this.$store.commit("updateUser", result.data.parent);
            this.persistedLocale = this.normalizeLocale(result.data.parent.language) || locale;
          } else {
            this.persistedLocale = locale;
          }
        }

        this.highestUnlockedStep = 2;
        this.currentStep = 2;
      } catch (err) {
        this.consoleLog(err);
        this.error = err.response?.data?.message || this.$t("error.general.connection");
        window.scrollTo(0, 0);
      } finally {
        this.savingLanguage = false;
      }
    },
    async submitForm() {
      const payload = this.getProfilePayload();

      this.error = "";
      this.success = "";

      if (!payload.firstName || !payload.lastName || !payload.nickName) {
        this.error = this.$t("error.general.requiredNeededError");
        window.scrollTo(0, 0);
        return;
      }

      this.savingProfile = true;

      try {
        await neoparentUsers.updateUserData(payload);
        this.userData = {
          ...this.userData,
          ...payload
        };
        this.syncStoreUser(payload);
        this.success = this.$t("success.profileUpdated");
        redirect("/");
      } catch (err) {
        this.consoleLog(err);
        this.error = err.response?.data?.message || this.$t("error.general.connection");
        window.scrollTo(0, 0);
      } finally {
        this.savingProfile = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.neoparent-new-profile {
  padding: 32px 16px;
}

.wizard-card {
  width: 100%;
  max-width: 560px;
}

.wizard-header {
  margin-bottom: 24px;
}

.wizard-header__content {
  margin-top: 20px;
}

.wizard-header__counter {
  color: #8a8a8a;
  font-size: 0.85rem;
  margin-bottom: 6px;
}

.wizard-header__title {
  color: #2b2b2b;
  font-size: 1.5rem;
  line-height: 1.3;
  margin: 0;
}

.wizard-progress {
  display: grid;
  gap: 12px;
}

.wizard-progress__step {
  align-items: center;
  background: #fff;
  border: 1px solid #e7b3c6;
  border-radius: 16px;
  color: #6b6b6b;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  text-align: left;
  transition: all 0.2s ease;
}

.wizard-progress__step:disabled {
  cursor: default;
  opacity: 0.7;
}

.wizard-progress__step--active,
.wizard-progress__step--completed {
  border-color: #e05484;
}

.wizard-progress__number {
  align-items: center;
  background: #f7d7e3;
  border-radius: 999px;
  color: #e05484;
  display: inline-flex;
  font-size: 0.9rem;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  min-width: 28px;
}

.wizard-progress__step--active .wizard-progress__number,
.wizard-progress__step--completed .wizard-progress__number {
  background: #e05484;
  color: #fff;
}

.wizard-progress__label {
  font-size: 0.95rem;
  font-weight: 600;
}

.wizard-step {
  background: #fff;
}

.language-switcher {
  margin-bottom: 24px;
}

.language-switcher__label {
  color: #6b6b6b;
  font-size: 0.95rem;
}

.wizard-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.wizard-actions--end {
  justify-content: flex-end;
}

.wizard-actions--between {
  justify-content: space-between;
}

.wizard-back {
  background: transparent;
  border: 1px solid #e05484;
  border-radius: 999px;
  color: #e05484;
  cursor: pointer;
  min-height: 38px;
  padding: 0 24px;
}

.wizard-back:disabled {
  border-color: #b5b5b5;
  color: #b5b5b5;
  cursor: not-allowed;
}

.status-message {
  color: #6b6b6b;
}

.error {
  color: #c62828;
}

@media (max-width: 600px) {
  .wizard-actions--between {
    align-items: stretch;
    flex-direction: column;
  }

  .wizard-back {
    width: 100%;
  }
}
</style>
